import type { KnownCantileverFileHosts } from 'features/knowledgeDocumentViewer/hooks/useCantileverFileHostsForDocument'
import { useCantileverFileHostsForDocument } from 'features/knowledgeDocumentViewer/hooks/useCantileverFileHostsForDocument'
import { useDocumentPageImage } from 'features/knowledgeDocumentViewer/hooks/useDocumentPageImage'

interface UseResolvedDocumentPageImageOptions {
  pageNumber?: number
  skipHiRes?: boolean
  knownHosts?: KnownCantileverFileHosts
}

interface UseResolvedDocumentPageImageResult {
  thumbnailUrl: string | undefined
  isThumbLoading: boolean
  hiResUrl: string | null
  isHiResLoading: boolean
  isHostsLoading: boolean
}

/**
 * Dual-read consumer API for page images.
 * When bqDocumentId is known (list + preview), UUID5 page hosts are derived
 * immediately — no Rolodex page-host wait. Tagged page hosts still apply when
 * provided via knownHosts (e.g. viewer CantileverPageHostsContext).
 */
export function useResolvedDocumentPageImage(
  systemDocumentEntryId: string | undefined,
  siteId: string,
  options?: UseResolvedDocumentPageImageOptions
): UseResolvedDocumentPageImageResult {
  const pageNumber = options?.pageNumber ?? 1
  const skipHiRes = options?.skipHiRes === true
  const knownHosts = options?.knownHosts
  const skipPageHostLookup =
    knownHosts?.skipPageHostLookup === true ||
    skipHiRes ||
    !!knownHosts?.bqDocumentId

  const {
    systemDocumentEntryId: resolvedEntryId,
    cantileverDocumentId,
    cantileverDocumentPageId,
    filename: resolvedFilename,
    bqDocumentId,
    isLoading: isHostsLoading,
  } = useCantileverFileHostsForDocument(
    systemDocumentEntryId,
    siteId,
    pageNumber,
    {
      ...knownHosts,
      skipPageHostLookup,
    }
  )

  const resolvedBqDocumentId = bqDocumentId ?? knownHosts?.bqDocumentId

  const { thumbnailUrl, isThumbLoading, hiResUrl, isHiResLoading } =
    useDocumentPageImage({
      systemDocumentEntryId: resolvedEntryId ?? systemDocumentEntryId,
      // When skipping Rolodex page lookup, only honor an explicitly known page host.
      cantileverDocumentPageId: skipPageHostLookup
        ? knownHosts?.cantileverDocumentPageId
        : cantileverDocumentPageId,
      cantileverDocumentId,
      siteId,
      bqDocumentId: resolvedBqDocumentId,
      filename: resolvedFilename ?? knownHosts?.filename,
      skipHiRes,
      pageNumber,
    })

  // Do not block paint on host Rolodex when UUID5 derive (or legacy) can proceed.
  const blockOnHosts =
    isHostsLoading &&
    !resolvedBqDocumentId &&
    !knownHosts?.cantileverDocumentPageId

  return {
    thumbnailUrl,
    isThumbLoading: blockOnHosts || isThumbLoading,
    hiResUrl,
    isHiResLoading: skipHiRes ? false : blockOnHosts || isHiResLoading,
    isHostsLoading: blockOnHosts,
  }
}
